import React, {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react';
import type { AxiosResponse } from 'axios';
import cx from 'classnames';
import { Field as FormikField, Formik } from 'formik';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';

import { Modal } from '@broadleaf/admin-components/dist/common';
import Currency from '@broadleaf/admin-components/dist/common/components/Currency';
import FormikError from '@broadleaf/admin-components/dist/form/components/FormikError';
import { Select } from '@broadleaf/admin-components/dist/common/components/Select';
import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import SubmissionError from '@broadleaf/admin-components/dist/common/components/SubmissionError';
import SVG from '@broadleaf/admin-components/dist/common/components/SVG';
import Image from '@broadleaf/admin-components/dist/common/elements/Image';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import {
  clearFormikErrors,
  setFormikErrors
} from '@broadleaf/admin-components/dist/form/utils/RequestErrorHelpers';
import { getAttribute } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import { queueFetch } from '@broadleaf/admin-components/dist/oms/components/OrderView/state/actions';
import { useInitialValuesForCreateReturnAction } from 'app/oms/hooks/useInitialValuesForCreateReturnAction';
import { findOrderItem } from '@broadleaf/admin-components/dist/oms/utils/OrderItemUtils';
import type { IFormik } from '@broadleaf/admin-components/dist/types/form';
import type { IMetadataAction } from '@broadleaf/admin-components/dist/types/metadata';

import {
  CreateReturnActionFields,
  CreateReturnActionInputProps,
  CreateReturnActionItemQuantityInputProps,
  CreateReturnActionItemQuantityProps,
  CreateReturnContext,
  CreateReturnModalFooter
} from '@broadleaf/admin-components/dist/oms/elements/CreateReturnAction';
import { StatusChangeItemReasonInputProps } from '@broadleaf/admin-components/dist/oms/elements';
import messages from '@broadleaf/admin-components/dist/oms/elements/CreateReturnAction/CreateReturnAction.messages';

/**
 * Override to fix a display issue on quantity input. This can be removed once the framework has been updated.
 */

export interface CreateReturnActionProps {
  actionDefinition: IMetadataAction;
  className?: string;
  handleClose?: Function;
  handleSuccess: Function;
  handleSubmit?: (
    values: any,
    formikBag: IFormik,
    submit: (values: any, formikBag: IFormik) => void
  ) => void;
  children?: ReactNode;
}

export const CreateReturnAction: React.FC<CreateReturnActionProps> = ({
  actionDefinition,
  className,
  handleClose,
  handleSuccess,
  handleSubmit,
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { disabled, dispatch } = useContext(CreateReturnContext);
  const statusChangeReasons = getAttribute(
    actionDefinition,
    'statusChangeReasons',
    []
  );
  return (
    <>
      <CreateReturnButton
        className={className}
        onClick={() => {
          !disabled && setIsOpen(true);
        }}
      >
        {actionDefinition.label}
      </CreateReturnButton>
      {isOpen && (
        <CreateReturnModal
          onClose={() => {
            setIsOpen(false);
            !!handleClose && handleClose();
          }}
          onSuccess={response => {
            dispatch(queueFetch());
            !!handleSuccess && handleSuccess(response);
          }}
          submitLabel={getAttribute(
            actionDefinition,
            'submitLabel',
            actionDefinition.label
          )}
          title={actionDefinition.label}
          statusChangeReasons={statusChangeReasons}
          handleSubmit={handleSubmit}
          children={children}
        />
      )}
    </>
  );
};

export interface CreateReturnActionButtonProps {
  className?: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  children?: ReactNode;
}

export const CreateReturnButton: FC<CreateReturnActionButtonProps> = ({
  children,
  className,
  onClick
}) => {
  const { disabled } = useContext(CreateReturnContext);
  return (
    <button
      className={cx('CreateReturnButton', { [className]: !!className })}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export interface CreateReturnActionModalProps {
  onClose: React.MouseEventHandler<HTMLElement>;
  onSuccess: (response: AxiosResponse<any, any>) => void;
  submitLabel: ReactNode;
  title: ReactNode;
  statusChangeReasons?: string[];
  handleSubmit?: (
    values: any,
    formikBag: IFormik,
    submit: (values: any, formikBag: IFormik) => void
  ) => void;
  children?: ReactNode; // added
}

export const CreateReturnModal: FC<CreateReturnActionModalProps> = ({
  onClose,
  onSuccess,
  submitLabel,
  title,
  statusChangeReasons,
  children,
  ...rest
}) => {
  const { contextParams, submitEndpoint, returnableItems } =
    useContext(CreateReturnContext);
  const initialValues = useInitialValuesForCreateReturnAction(returnableItems);
  const formatMessage = useFormatMessage();

  const submit = useCallback(
    async (values, formik) => {
      formik.setSubmitting(true);
      try {
        const response = await request(
          { method: 'post', ...submitEndpoint, data: values },
          contextParams
        );
        clearFormikErrors(formik);
        onSuccess(response);
      } catch (err) {
        setFormikErrors(err, formik);
      } finally {
        formik.setSubmitting(false);
      }
    },
    [contextParams, submitEndpoint, onSuccess]
  );

  const handleSubmit = useCallback(
    async (values, formik) => {
      if (rest.handleSubmit) {
        return rest.handleSubmit(values, formik, submit);
      } else {
        // Aurora customization, do not use a confirm modal
        submit(values, formik);
        // End Aurora customization
      }
    },
    [formatMessage, submit]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {formik => (
        <SimpleModal
          closeOnClickOutside={false}
          header={
            <Fragment>
              <Modal.Header.Title>{title}</Modal.Header.Title>
              <FormikError className="tw-ml-2 tw-mr-auto" formik={formik} />
              {onClose && <Modal.Close onClose={onClose} />}
            </Fragment>
          }
          footer={
            <CreateReturnModalFooter
              formik={formik}
              submitLabel={submitLabel}
            />
          }
          isOpen
          onClose={onClose}
          size="lg"
          title={title}
        >
          <CreateReturnFields
            formik={formik}
            statusChangeReasons={statusChangeReasons}
          />
          {children}
        </SimpleModal>
      )}
    </Formik>
  );
};

export const CreateReturnFields: FC<CreateReturnActionFields> = ({
  formik,
  statusChangeReasons
}) => {
  // get order item that matches the fulfillment item to get the thumbnail and name
  const { order, returnableItems } = useContext(CreateReturnContext);
  const { items } = formik.values;

  return (
    <div>
      {map(items, (returnItem, i: number) => {
        const rootPath = `items[${i}]`;
        const name = `${rootPath}.quantity`;
        const reasonName = `${rootPath}.primaryReturnReason`;
        const fulfillmentItem = find(returnableItems, {
          id: returnItem.orderFulfillmentItemId
        });
        const orderItem = findOrderItem(order.orderItems, {
          id: fulfillmentItem.orderItemId
        });
        return (
          <React.Fragment key={i}>
            {i > 0 && <hr className="tw-my-4 tw-border-gray-300" />}
            <CreateReturnActionItemQuantityField
              formik={formik}
              fulfillmentItem={fulfillmentItem}
              name={name}
              reasonChangingStatusName={reasonName}
              orderItem={orderItem}
              value={returnItem.quantity}
              statusChangeReasons={statusChangeReasons}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const CreateReturnActionItemQuantityField: FC<
  CreateReturnActionItemQuantityProps
> = ({
  formik,
  fulfillmentItem,
  name,
  orderItem,
  value,
  reasonChangingStatusName,
  statusChangeReasons
}) => {
  const thumbnailUrl = getThumbnailUrl(orderItem);
  const { quantity, unitMerchandisePrice } = fulfillmentItem;

  return (
    <div className="tw-flex tw-flex-col tw-items-center md:tw-flex-row md:tw-items-start">
      <div className="tw-mb-3 tw-h-32 tw-w-32 tw-flex-initial md:tw-mb-0 md:tw-h-24 md:tw-w-24">
        <Image
          alt={orderItem.name}
          className="tw-max-h-full tw-w-full"
          fallback={
            <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-gray-100">
              <SVG
                className="tw-h-1/2 tw-w-1/2 tw-fill-current tw-text-gray-400"
                name="photo"
              />
            </div>
          }
          src={thumbnailUrl}
        />
      </div>
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-row tw-overflow-hidden md:tw-ml-3 md:tw-w-auto">
        <div className="tw-flex-1 tw-overflow-hidden">
          <div className="tw-truncate tw-text-lg tw-font-semibold tw-leading-tight tw-text-gray-800">
            {orderItem.name}
          </div>
          <div className="tw-truncate tw-text-sm tw-text-gray-700">
            {orderItem.sku}
          </div>
          <div className="tw-mt-1 tw-text-xs tw-text-gray-700">
            <Currency
              currency={unitMerchandisePrice.currency}
              value={unitMerchandisePrice.amount}
            />
            {quantity > 1 && ` x ${quantity}`}
          </div>
          <StatusChangeItemReasonInput
            formik={formik}
            options={statusChangeReasons}
            name={reasonChangingStatusName}
          />
        </div>
        <div className="tw-ml-3 tw-flex-initial">
          <CreateReturnActionItemQuantityInput
            formik={formik}
            max={quantity}
            name={name}
            value={value}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateReturnActionItemQuantityInput: FC<
  CreateReturnActionItemQuantityInputProps
> = ({ formik, max, name, value }) => {
  const formatMessage = useFormatMessage();
  const error = get(formik.errors, name);
  const touched = get(formik.touched, name);
  const showError = !!touched && !!error;
  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-whitespace-nowrap">
      {showError && <SubmissionError className="tw-mr-2" errors={[error]} />}
      <FormikField
        max={max}
        min="0"
        name={name}
        showError={showError}
        step="1"
        type="number"
        value={value}
        component={AuroraCreateReturnActionInput}
      />
      <span className="tw-ml-1">
        {formatMessage(messages.quantityOf, { max })}
      </span>
    </div>
  );
};

const StatusChangeItemReasonInput: FC<StatusChangeItemReasonInputProps> = ({
  formik,
  options,
  name
}) => {
  const formatMessage = useFormatMessage();
  const error = get(formik.errors, name);
  const touched = get(formik.touched, name);
  const showError = !!touched && !!error;

  if (!options || !options.length) {
    return null;
  }

  return (
    <div className="tw-relative tw-mb-2 tw-mt-4 tw-flex tw-items-center tw-px-1">
      <Select
        className={cx({
          'tw-border-gray-300': !showError,
          'tw-border-red-600': showError
        })}
        menuPosition="fixed"
        placeholder={formatMessage(messages.selectReason)}
        options={options}
        onChange={option => formik.setFieldValue(name, option.value)}
      />
      {showError && <SubmissionError className="tw-mr-2" errors={[error]} />}
    </div>
  );
};

// Aurora patch
export const AuroraCreateReturnActionInput: FC<
  CreateReturnActionInputProps
> = ({ field, showError = true, ...props }) => {
  return (
    <input
      {...props} // existing props go first
      className={cx('tw-mr-1 tw-rounded tw-border tw-px-2 tw-py-1', {
        'tw-border-gray-300': !showError,
        'tw-border-red-600': showError
      })}
      {...field}
    />
  );
};

function getThumbnailUrl(orderItem) {
  return get(orderItem, 'imageAsset.contentUrl');
}

export default CreateReturnAction;
